import { Vue } from 'vue-property-decorator';

import { ORDER_STATE_ID } from '@/constants/order-state-id';
import { PAYMENT_METHOD_ID, PaymentMethod } from '@/core/payment-methods/constants';
import { DIGITAL_PAYMENT_METHOD_GROUP } from '@/core/payment-methods/constants/groups';
import { Customer, Order, Merchant } from '@/views/checkout/interfaces';
import { TokenData, ChargeDetails } from '@/views/checkout/components/charge-dialog/interfaces';
import { CurrencyInfo } from '@/core/currency/interfaces';
import { getPaymentMethodName } from '@/core/payment-methods/utilities';
import { DIRECT_DEBIT_FAILED_EVENT_IDS } from '@/constants/event-id';

export function trackCheckoutInitiated(
    customer: Customer,
    order: Order,
    merchant: Merchant,
    currency: CurrencyInfo,
    eventName: string = 'checkout_initiated'
) {
    const convertedMethods: Array<string> = [];

    merchant.paymentMethods.forEach(
        (paymentMethodId) => convertedMethods.push(getPaymentMethodName(paymentMethodId) || '')
    );

    Vue.$gtm.triggerEvent({
        currency: currency?.name || '',
        amount: order.amount || 0,
        event: eventName,
        channel: 'no info available',
        service_id: 'no info available',
        merchant_id: merchant.merchantId,
        merchant_name: merchant.tradeName,
        merchant_mcc: 'no info available',
        payment_methods: convertedMethods.toString(),
        merchant_country: merchant.countryCode,
        customer_country: customer.countryCode || '',
        statement_descriptor: !!order.descriptorDynamicSuffix,
        cardtokens_specified: customer.cards?.length ? 'yes' : 'no',
        disabled_exact_amount: order.editableAmount,
        description_specified: order.notes !== null,
        email_prespecified: customer.email !== null,
        name_prespecified: customer.fullName !== null,
        is_paid: order.stateId === ORDER_STATE_ID.Paid,
        language_presented: order.requestLang.split('-')[0],
        is_expired: order.stateId === ORDER_STATE_ID.Expired,
        allow_recurring: 'no info available although boolean response needed',
        preauthorization: 'no info available although boolean response needed',
        installments_offered: 'no info available although boolean response needed',
        saved_cards_displayed: JSON.parse(Vue.$localStorage.getItem('stored-cards') || '[]')?.length > 0 || !!customer.cards?.length,
        initiated_by: 'no info available (shopify, woocommerce, payment notification, quickpay, etc)'
    });
}

export function trackPaymentCancelled() {
    Vue.$gtm.triggerEvent({
        event: 'payment_cancelled',
        language: Vue.$localStorage.getItem('language')
    });
}

export function trackPaymentSubmitted(
    chargeDetails: ChargeDetails | null,
    tokenData: TokenData | null,
    paymentMethod: PaymentMethod | null
) {
    const paymentMethodId: PAYMENT_METHOD_ID | null = tokenData?.paymentMethodId ?? paymentMethod?.id ?? null;

    Vue.$gtm.triggerEvent({
        event: 'payment_submitted',
        payment_method: getTrackerPaymentMethod(paymentMethodId),
        language: Vue.$localStorage.getItem('language'),
        installments: !!chargeDetails?.installments,
        amount_changed: '(true/false)',
        saved_card_used: !!chargeDetails?.creditCardId ||
            (!!chargeDetails?.token && !(paymentMethod && DIGITAL_PAYMENT_METHOD_GROUP.includes(paymentMethod))),
        card_details_saved: !!chargeDetails?.rememberCard
    });
}

export function trackPaymentResponse(
    success: boolean,
    paymentMethodId: PAYMENT_METHOD_ID,
    correlationId?: string | null,
    eventId?: string | null,
    redirectUrl?: string | null
) {
    let eciCode: string | null = '';

    try {
        if (redirectUrl) {
            const url = new URL(redirectUrl?.toLocaleLowerCase());
            const params = new URLSearchParams(url.search);

            eciCode = params.get('eci');
        }
    } catch (e) {
        console.log(e);
    }

    if (success) {
        trackPaymentSuccess(paymentMethodId, eventId, eciCode, correlationId);
    } else {
        trackPaymentFailure(paymentMethodId, eventId, eciCode, correlationId);
    }
}

export function trackPaymentSuccess(
    paymentMethodId: PAYMENT_METHOD_ID | null,
    eventId?: string | null,
    eciCode?: string | null,
    correlationId?: string | null
) {
    Vue.$gtm.triggerEvent({
        event: 'payment_success',
        payment_method: getTrackerPaymentMethod(paymentMethodId),
        status_id: eventId || 'n/a',
        transaction_type_id: '',
        eci: eciCode || 'n/a',
        threeDs_flow: '(yes, no, n/a)',
        threeDs_challenge: '(yes, no, n/a)',
        threeDs_status: '(yes, attempt, no, rejected, unavailable, n/a)',
        correlation_id: correlationId || 'n/a'
    });
}

export function trackPaymentFailure(
    paymentMethodId: PAYMENT_METHOD_ID | null,
    eventId?: string | null,
    eciCode?: string | null,
    correlationId?: string | null
) {
    Vue.$gtm.triggerEvent({
        event: 'payment_failure',
        payment_method: getTrackerPaymentMethod(paymentMethodId),
        error_code: 'n/a',
        event_id: eventId || 'n/a',
        eci: eciCode || 'n/a',
        threeDs_flow: '(yes, no, n/a)',
        threeDs_challenge: '(yes, no, n/a)',
        threeDs_status: '(yes, attempt, no, rejected, unavailable, n/a)',
        correlation_id: correlationId || 'n/a'
    });
}

export function trackPaymentCardFormError(errors) {
    Vue.$gtm.triggerEvent({
        event: 'payment_card_form_error',
        email: errors.emailAddress,
        card_name: errors.cardHolder,
        card_number: errors.cardNumber,
        card_expiry: errors.cardExpiration,
        card_cvv: errors.cardCvv
    });
}

export function trackSavedPaymentCardFormError(errors) {
    Vue.$gtm.triggerEvent({
        event: 'payment_card_form_error',
        email: 'not applicable for saved cards',
        card_name: 'not applicable for saved cards',
        card_number: 'not applicable for saved cards',
        card_expiry: 'not applicable for saved cards',
        saved_card_cvv: errors.cardCvv
    });
}

export function trackThreeDsInitiation(correlationId?: string) {
    Vue.$gtm.triggerEvent({
        event: '3ds_initiated',
        correlation_id: correlationId || 'n/a'
    });
}

export function trackProcessingFailure(eventId: number = 0, correlationId?: string) {
    let event: string = 'decline_recovery_initiated';

    if (DIRECT_DEBIT_FAILED_EVENT_IDS.includes(eventId)) {
        event = 'payment_sepa_dd_error';
    }

    Vue.$gtm.triggerEvent({
        event,
        correlation_id: correlationId || 'n/a',
        event_id: eventId
    });
}

export function trackPaymentWaitingQueue(event: string) {
    Vue.$gtm.triggerEvent({
        event
    });
}

function getTrackerPaymentMethod(id?: PAYMENT_METHOD_ID | null) {
    if (id === null || typeof id === 'undefined') {
        return 'n/a';
    }

    return getPaymentMethodName(id!);
}
